import { isUndefined } from 'underscore';

import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '~commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '~commonInfra/contentActions/MultipleItemResult';
import { openReactConfirmationDialog, ReactConfirmationDialogAreYouSureBody } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { LinkedTasks } from '~src/linkedTasks/resources/LinkedTasks';
import { DeleteSchedulesActionService } from '~src/schedule/actions/DeleteSchedulesAction';

import { getViewInfos } from '../../app/sagas/spaces/SpacesSagaHelpers';
import { DeleteCollectionData } from '../../app/spaces/Collections/CollectionModels';
import { CollectionsResourceQuery } from '../../app/spaces/Collections/ResourceQuery/CollectionsResourceQuery';
import { Formatters, Messages } from '../../codegen/Localize';
import { getExplorableItemDefinition } from '../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { IDataSourceInfo } from '../../datasource/models/IDataSourceInfo';
import { DataSources } from '../../datasource/resources/Datasources';
import { DataSourceErrors } from '../../datasource/services/DataSourceErrors';
import { DataAlerts } from '../../dda/resources/DataAlerts';
import { DeleteRunFlowTasksAction } from '../../flow/actions/DeleteRunFlowTasksAction';
import { Flows } from '../../flow/resources/Flows';
import { RunFlowTasks } from '../../flow/resources/RunFlowTasks';
import { FlowErrors } from '../../flow/services/FlowErrors';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { Metrics } from '../../metrics/resources/Metrics';
import { MetricErrors } from '../../metrics/services/MetricErrors';
import { Projects } from '../../projects/resources/Projects';
import { PublishedConnections } from '../../publishedConnection/resources/PublishedConnections';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { L10n } from '../../ts/L10n/L10n';
import { CollectionMapper } from '../../ts/mappers/CollectionMapper';
import { ExplorableItemType } from '../../ts/models/ContentType';
import { FlowListItemInfo } from '../../ts/models/FlowInfo';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { IDataAlertBase } from '../../ts/models/IDataAlertInfo';
import { IDataRoleInfo } from '../../ts/models/IDataRoleInfo';
import { ILensInfo } from '../../ts/models/ILensInfo';
import { IProjectInfo } from '../../ts/models/IProjectInfo';
import { IPublishedConnectionInfo } from '../../ts/models/IPublishedConnectionInfo';
import { IViewDetailInfo, IViewInfo } from '../../ts/models/IViewInfo';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { Set } from '../../ts/models/Set';
import { DataRoles } from '../../ts/resources/Dataroles';
import { Lenses } from '../../ts/resources/Lenses';
import { SchedulesService } from '../../ts/resources/Schedules';
import { CustomViews } from '../../ts/ServerApi/CustomViews/CustomViews';
import { ICustomViewInfo } from '../../ts/ServerApi/CustomViews/ICustomViewInfo';
import { ContentDraftStatus } from '../../ts/ServerApi/types/ContentDraft';
import { CollectionErrors } from '../../ts/services/CollectionErrors';
import { CustomViewErrors } from '../../ts/services/customViews/CustomViewErrors';
import { DataRoleErrors } from '../../ts/services/DataRoleErrors';
import { LensErrors } from '../../ts/services/LensErrors';
import { PublishedConnectionErrors } from '../../ts/services/PublishedConnectionErrors';
import { ScheduleErrorsService } from '../../ts/services/ScheduleErrors';
import { Views } from '../../views/resources/Views';
import { IWorkbookInfo, IWorkbookParentInfo } from '../../workbook/models/IWorkbookInfo';
import { Workbooks } from '../../workbook/resources/Workbooks';
import { WorkbookErrors } from '../../workbook/services/WorkbookErrors';
import { DataAlertErrors } from '../services/DataAlertErrors';
import { ProjectErrors } from './ProjectErrors';
import { ViewErrors } from './ViewErrors';
import _ = require('underscore');
import { IConnectedApplicationInfo } from '~src/connectedApplications/models/ConnectedApplicationInfo';
import { ConnectedApplications } from '~src/connectedApplications/resources/ConnectedApplicationsResource';

export interface ContentTypeAndID {
  contentType: ExplorableItemType;
  id: string;
}
export interface IDeleteContentsResult extends IResult {
  deletedItems: ContentTypeAndID[];
}

// Determine if any workbooks will be deleted as a result of deleting the selected views
function determineWorkbooksToBeDeleted(views: Set<IViewInfo | IViewDetailInfo>): IWorkbookParentInfo[] {
  const workbooksToDelete: IWorkbookParentInfo[] = [];
  const workbookViewCount: _.Dictionary<number> = {};
  views.each((view: IViewInfo) => {
    let viewCount = workbookViewCount[view.workbook.id] || 0;
    viewCount++;
    if (viewCount === view.workbook.numSheets) {
      workbooksToDelete.push(view.workbook);
    }
    workbookViewCount[view.workbook.id] = viewCount;
  });
  return workbooksToDelete;
}

function confirmDeleteContents(contents: IContentInfo[]): Promise<IDeleteContentsResult> {
  let projects: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let workbooks: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let dataRoles: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let lenses: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let datasources: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let views: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let flows: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let publishedConnections: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let collections: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let metrics: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let alerts: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let schedules: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let runFlowTasks: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let customViews: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let connectedApplications: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  contents.forEach((contentItem) => {
    switch (contentItem.contentType) {
      case 'project':
        projects.add(contentItem);
        break;
      case 'workbook':
        workbooks.add(contentItem);
        break;
      case 'dataRole':
        dataRoles.add(contentItem);
        break;
      case 'lens':
        lenses.add(contentItem);
        break;
      case 'datasource':
        datasources.add(contentItem);
        break;
      case 'view':
        views.add(contentItem);
        break;
      case 'flow':
        flows.add(contentItem);
        break;
      case 'publishedConnection':
        publishedConnections.add(contentItem);
        break;
      case 'collection':
        collections.add(contentItem);
        break;
      case 'metric':
        metrics.add(contentItem);
        break;
      case 'dataalert':
        alerts.add(contentItem);
        break;
      case 'schedule':
        schedules.add(contentItem);
        break;
      case 'runFlowTask':
        runFlowTasks.add(contentItem);
        break;
      case 'customView':
        customViews.add(contentItem);
        break;
      case 'connectedApplication':
        connectedApplications.add(contentItem);
        break;
      default:
        break;
    }
  });

  let workbooksToBeDeleted: IWorkbookParentInfo[] = [];
  let dialogBody: string = '';
  let determineWorkbooksToBeDeletedPromise: Promise<IViewInfo[]>;
  if (views.size() > 0) {
    determineWorkbooksToBeDeletedPromise = getViewInfos(views.toArray());
  } else {
    determineWorkbooksToBeDeletedPromise = Promise.resolve([] as IViewInfo[]);
  }

  return determineWorkbooksToBeDeletedPromise.then((viewDetailArray: IViewInfo[]) => {
    if (views.size() > 0) {
      workbooksToBeDeleted = determineWorkbooksToBeDeleted(Set.fromIdItemArray(viewDetailArray));
    }
    dialogBody = buildConfirmationDialog(contents, workbooksToBeDeleted);
    return buildDialogBox(
      dialogBody,
      contents,
      projects,
      workbooks,
      dataRoles,
      lenses,
      datasources,
      views,
      flows,
      publishedConnections,
      collections,
      metrics,
      alerts,
      schedules,
      runFlowTasks,
      customViews,
      connectedApplications);
  });
}

function buildDialogBox(
  dialogBody: string,
  contents: IContentInfo[],
  projects: Set<IContentInfo>,
  workbooks: Set<IContentInfo>,
  dataRoles: Set<IContentInfo>,
  lenses: Set<IContentInfo>,
  datasources: Set<IContentInfo>,
  views: Set<IContentInfo>,
  flows: Set<IContentInfo>,
  publishedConnections: Set<IContentInfo>,
  collections: Set<IContentInfo>,
  metrics: Set<IContentInfo>,
  alerts: Set<IContentInfo>,
  schedules: Set<IContentInfo>,
  runFlowTasks: Set<IContentInfo>,
  customViews: Set<IContentInfo>,
  connectedApplications: Set<IContentInfo>
): Promise<IDeleteContentsResult> {

  const initialModalInstance = openReactConfirmationDialog<void, IDeleteContentsResult>({
    title: Messages.deleteMessage(),
    message: dialogBody,
    paramsForAction: undefined,
    actionName: Messages.confirmDeleteButton({ ITEM_COUNT: contents.length, ITEM_COUNT_LOC: Formatters.formatCount(contents.length) }),
    //If the set of content items included any projects, we defer deletion until a second 'Are you sure' confirmation is given. If no projects are being deleted, proceed after the user clicks 'Delete'
    action: () => {
      return projects.size() > 0
        ? null
        : deleteContent(projects, workbooks, dataRoles, lenses, datasources, views, flows, publishedConnections, collections, metrics, alerts, schedules, runFlowTasks, customViews, connectedApplications);
    },
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });

  return initialModalInstance.result.then((result) => {
    initialModalInstance.dismiss();

    if (projects.size() === 0) {
      //Content is deleted, no second confirmation is needed
      return result;
    }

    const modalInstance = openReactConfirmationDialog<void, IDeleteContentsResult>({
      bodyComponent: ReactConfirmationDialogAreYouSureBody,
      message: Messages.actionNotUndoable(),
      paramsForAction: undefined,
      actionName: Messages.confirmDeleteProjectsDefinitely({ PROJECT_COUNT: projects.size() }),
      action: () => {
        return deleteContent(projects, workbooks, dataRoles, lenses, datasources, views, flows, publishedConnections, collections, metrics, alerts, schedules, runFlowTasks, customViews, connectedApplications);
      },
      isActionDelete: true,
      centerActionButtons: true,
      dialogWidth: TabDialogWidth.width375px
    });
    return modalInstance.result;
  });
}

function deleteContent(
  projects: Set<IContentInfo>,
  workbooks: Set<IContentInfo>,
  dataRoles: Set<IContentInfo>,
  lenses: Set<IContentInfo>,
  datasources: Set<IContentInfo>,
  views: Set<IContentInfo>,
  flows: Set<IContentInfo>,
  publishedConnections: Set<IContentInfo>,
  collections: Set<IContentInfo>,
  metrics: Set<IContentInfo>,
  alerts: Set<IContentInfo>,
  schedules: Set<IContentInfo>,
  runFlowTasks: Set<IContentInfo>,
  customViews: Set<IContentInfo>,
  connectedApplications: Set<IContentInfo>
): Promise<IDeleteContentsResult> {
  let aggregateResult: IDeleteContentsResult[] = [];
  let outstandingPromises: Promise<IResult>[] = [];

  if (projects.size() > 0) {
    const deleteProjects = Projects.del(projects.map(item => item.id));
    outstandingPromises.push(deleteProjects);
    deleteProjects.then(
      (result: IResult) => {
        const deletedProjects: ContentTypeAndID[] = getActuallyDeletedItems(projects, result);
        aggregateResult.push({ deletedItems: deletedProjects, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          projects,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteProjectsSuccess),
            error: standardActionErrorMsg(Messages.deleteProjectsError),
          },
          (errorItem: IErrorItem<IProjectInfo>) => ProjectErrors.errorDetailsFor(errorItem));
      });
  }

  if (workbooks.size() > 0) {
    const deleteWorkbooks = Workbooks.del(workbooks.map(item => item.id));
    outstandingPromises.push(deleteWorkbooks);
    deleteWorkbooks.then(
      (result: IResult) => {
        const deletedWorkbooks: ContentTypeAndID[] = getActuallyDeletedItems(workbooks, result);
        aggregateResult.push({ deletedItems: deletedWorkbooks, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          workbooks,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteWorkbooksSuccess),
            error: standardActionErrorMsg(Messages.deleteWorkbooksError),
          },
          (errorItem: IErrorItem<IWorkbookInfo>) => WorkbookErrors.errorDetailsFor(errorItem));
      });
  }

  if (dataRoles.size() > 0) {
    const deleteDataRoles = DataRoles.deleteDataRoles(dataRoles.map(item => item.id));
    outstandingPromises.push(deleteDataRoles);
    deleteDataRoles.then(
      (result: IResult) => {
        const deletedDRs: ContentTypeAndID[] = getActuallyDeletedItems(dataRoles, result);
        aggregateResult.push({ deletedItems: deletedDRs, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          dataRoles,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteDataRolesSuccess),
            error: standardActionErrorMsg(Messages.deleteDataRolesError),
          },
          (errorItem: IErrorItem<IDataRoleInfo>) => DataRoleErrors.errorDetailsFor(errorItem));
      });
  }

  if (lenses.size() > 0) {
    const deleteLenses = Lenses.deleteLenses(lenses.map(item => item.id));
    outstandingPromises.push(deleteLenses);
    deleteLenses.then(
      (result: IResult) => {
        const deletedLenses: ContentTypeAndID[] = getActuallyDeletedItems(lenses, result);
        aggregateResult.push({ deletedItems: deletedLenses, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          lenses,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteLensesSuccess),
            error: standardActionErrorMsg(Messages.deleteLensesError),
          },
          (errorItem: IErrorItem<ILensInfo>) => LensErrors.errorDetailsFor(errorItem));
      });
  }

  if (datasources.size() > 0) {
    const deleteDatasources = DataSources.del(datasources.map(item => item.id));
    outstandingPromises.push(deleteDatasources);
    deleteDatasources.then(
      (result: IResult) => {
        const deletedDSs: ContentTypeAndID[] = getActuallyDeletedItems(datasources, result);
        aggregateResult.push({ deletedItems: deletedDSs, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          datasources,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteDatasourcesSuccess),
            error: standardActionErrorMsg(Messages.deleteDatasourcesError),
          },
          (errorItem: IErrorItem<IDataSourceInfo>) => DataSourceErrors.errorDetailsFor(errorItem));
      });
  }

  if (views.size() > 0) {
    const deleteViews = Views.hide(views.map(item => item.id));
    outstandingPromises.push(deleteViews);
    deleteViews.then(
      (result: IResult) => {
        const deletedViews: ContentTypeAndID[] = getActuallyDeletedItems(views, result);
        aggregateResult.push({ deletedItems: deletedViews, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          views,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteViewsSuccess),
            error: standardActionErrorMsg(Messages.deleteViewsError),
          },
          (errorItem: IErrorItem<IViewInfo>) => ViewErrors.errorDetailsFor(errorItem));
      });
  }

  if (flows.size() > 0) {
    const actualFlows = flows.filter(flow => {
      return flow.draftStatus !== ContentDraftStatus.unpublished;
    });
    if (actualFlows.size() > 0) {
      const deleteFlows = Flows.deleteFlows(actualFlows.map(item => item.id));
      outstandingPromises.push(deleteFlows);
      deleteFlows.then(
        (result: IResult) => {
          const deletedFlows: ContentTypeAndID[] = getActuallyDeletedItems(actualFlows, result);
          aggregateResult.push({ deletedItems: deletedFlows, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult(
            actualFlows,
            result,
            {
              success: standardActionSuccessMsg(Messages.deleteFlowsSuccess),
              error: standardActionErrorMsg(Messages.deleteFlowsError),
            },
            (errorItem: IErrorItem<FlowListItemInfo>) => FlowErrors.errorDetailsFor(errorItem));
        });
    }

    const flowDrafts = flows.filter(flow => {
      return flow.draftStatus === ContentDraftStatus.unpublished;
    });
    if (flowDrafts.size() > 0) {
      const deleteFlowDrafts = Flows.deleteFlowDrafts(flowDrafts.map(item => item.id));
      outstandingPromises.push(deleteFlowDrafts);
      deleteFlowDrafts.then(
        (result: IResult) => {
          const deletedFlows: ContentTypeAndID[] = getActuallyDeletedItems(flowDrafts, result);
          aggregateResult.push({ deletedItems: deletedFlows, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult(
            flowDrafts,
            result,
            {
              success: standardActionSuccessMsg(Messages.deleteFlowDraft_actionSuccess),
              error: standardActionErrorMsg(Messages.deleteFlowDraft_actionFailed),
            },
            (errorItem: IErrorItem<FlowListItemInfo>) => FlowErrors.errorDetailsFor(errorItem));
        });
    }
  }

  if (publishedConnections.size() > 0) {
    const deletePublishedConnections = PublishedConnections.del(publishedConnections.map(item => item.id));
    outstandingPromises.push(deletePublishedConnections);
    deletePublishedConnections.then(
      (result: IResult) => {
        const deletedPublishedConnections: ContentTypeAndID[] = getActuallyDeletedItems(publishedConnections, result);
        aggregateResult.push({ deletedItems: deletedPublishedConnections, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          publishedConnections,
          result,
          {
            success: standardActionSuccessMsg(Messages.deletePublishedConnectionsSuccess),
            error: standardActionErrorMsg(Messages.deletePublishedConnectionsError),
          },
          (errorItem: IErrorItem<IPublishedConnectionInfo>) => PublishedConnectionErrors.errorDetailsFor(errorItem));
      });
  }

  if (collections.size() > 0) {
    const deleteCollectionsPromise = (new CollectionsResourceQuery()).delete(collections.map(item => item.luid)).then(
      (result: DeleteCollectionData): IResult => {
        return CollectionMapper.mapDeleteCollectionsDataToIResult(result, collections);
      }
    );
    outstandingPromises.push(deleteCollectionsPromise);
    deleteCollectionsPromise.then(
      (result: IResult) => {
        const deletedCollections: ContentTypeAndID[] = getActuallyDeletedItems(collections, result);
        aggregateResult.push({ deletedItems: deletedCollections, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          collections,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteCollectionsSuccess),
            error: standardActionErrorMsg(Messages.deleteCollectionsError),
          },
          (errorItem: IErrorItem<IContentInfo<'collection'>>) => CollectionErrors.errorDetailsFor(errorItem));
      }).catch((error) => {
        return { errors: [{ code: error.data.errorCode }] } as IResult;
      });
  }

  if (alerts.size() > 0) {
    const deleteAlerts = DataAlerts.del(alerts.map(item => item.id));
    outstandingPromises.push(deleteAlerts);
    deleteAlerts.then(
      (result: IResult) => {
        const deletedAlerts: ContentTypeAndID[] = getActuallyDeletedItems(alerts, result);
        aggregateResult.push({ deletedItems: deletedAlerts, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult<IDataAlertBase>(
          alerts as Set<IDataAlertBase>,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteDataAlertsSuccess),
            error: standardActionErrorMsg(Messages.deleteDataAlertsError),
          },
          (errorItem: IErrorItem<IDataAlertBase>) => DataAlertErrors.errorDetailsFor(errorItem)
        );
      });
  }

  if (schedules.size() > 0) {
    const deleteSchedules = SchedulesService
      .del(schedules.map(item => item.id));
    outstandingPromises.push(deleteSchedules);
    deleteSchedules.then(
      (result: IResult) => {
        const deleteSchedules: ContentTypeAndID[] = getActuallyDeletedItems(schedules, result);
        aggregateResult.push({ deletedItems: deleteSchedules, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult<IScheduleInfo>(
          schedules as unknown as Set<IScheduleInfo>,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteSchedulesSuccess),
            error: standardActionErrorMsg(Messages.deleteSchedulesError),
          },
          (errorItem: IErrorItem<IScheduleInfo>) => ScheduleErrorsService.errorDetailsFor(errorItem));
      });
  }

  if (runFlowTasks.size() > 0) {
    // RunFlowTasks covers both 'FlowTask' and 'LinkedTask' scenarios
    const flowTasks = runFlowTasks.filter(item => (item as unknown as RunFlowTaskInfo).taskType === 'FlowTask');
    const linkedTasks = runFlowTasks.filter(item => (item as unknown as RunFlowTaskInfo).taskType === 'LinkedTask');
    if (flowTasks.size() > 0) {
      const deleteRunFlowTasks = RunFlowTasks.del(flowTasks.map(item => item.id));
      outstandingPromises.push(deleteRunFlowTasks);
      deleteRunFlowTasks.then(
        (result: IResult) => {
          const deletedRunFlowTasks: ContentTypeAndID[] = getActuallyDeletedItems(flowTasks, result);
          aggregateResult.push({ deletedItems: deletedRunFlowTasks, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult<RunFlowTaskInfo>(
            flowTasks as unknown as Set<RunFlowTaskInfo>,
            result,
            {
              success: standardActionSuccessMsg(Messages.deleteRunFlowTasksSuccess),
              error: standardActionErrorMsg(Messages.deleteRunFlowTasksError),
            },
            (errorItem: IErrorItem<RunFlowTaskInfo>) => DeleteRunFlowTasksAction.errorDetailFor(errorItem));
        });
    }
    if (linkedTasks.size() > 0) {
      const deleteLinkedTasks = LinkedTasks.deleteLinkedTasks(linkedTasks.map(item => (item as unknown as RunFlowTaskInfo).linkedTaskId));
      outstandingPromises.push(deleteLinkedTasks);
      deleteLinkedTasks.then(
        (result: IResult) => {
          const deletedLinkedTasks: ContentTypeAndID[] = getActuallyDeletedItems(linkedTasks, result);
          aggregateResult.push({ deletedItems: deletedLinkedTasks, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult<RunFlowTaskInfo>(
            linkedTasks as unknown as Set<RunFlowTaskInfo>,
            result,
            {
              success: standardActionSuccessMsg(Messages.deleteLinkedTasksSuccess),
              error: standardActionErrorMsg(Messages.deleteLinkedTasksError),
            },
            // todo jjia: linked tasks errors
            (errorItem: IErrorItem<RunFlowTaskInfo>) => null);//DeleteRunFlowTasksAction.errorDetailFor(errorItem));
        });
    }
  }

  if (metrics.size() > 0) {
    const deleteMetrics = Metrics.del(metrics.map(item => item.id));
    outstandingPromises.push(deleteMetrics);
    deleteMetrics.then(
      (result: IResult) => {
        const deletedMetrics: ContentTypeAndID[] = getActuallyDeletedItems(metrics, result);
        aggregateResult.push({ deletedItems: deletedMetrics, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          metrics,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteMetricsSuccess),
            error: standardActionErrorMsg(Messages.deleteMetricsError),
          },
          (errorItem: IErrorItem<IMetricInfo>) => MetricErrors.errorDetailsFor(errorItem)
        );
      });
  }

  if (customViews.size() > 0) {
    const deleteCustomViews = CustomViews.del(customViews.map(item => item.id));
    outstandingPromises.push(deleteCustomViews);
    deleteCustomViews.then(
      (result: IResult) => {
        const deletedCustomViews: ContentTypeAndID[] = getActuallyDeletedItems(customViews, result);
        aggregateResult.push({ deletedItems: deletedCustomViews, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          customViews as unknown as Set<ICustomViewInfo>,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteCustomViewsSuccess),
            error: standardActionErrorMsg(Messages.deleteCustomViewsError),
          },
          (errorItem: IErrorItem<ICustomViewInfo>) => CustomViewErrors.errorDetailsFor(errorItem)
        );
      });
  }

  if (connectedApplications.size() > 0) {
    const deleteConnectedApplications =
      ConnectedApplications.del(connectedApplications.map(item => item.id));
    outstandingPromises.push(deleteConnectedApplications);
    deleteConnectedApplications.then(
      (result: IResult) => {
        const deletedConnectedApplications: ContentTypeAndID[] = getActuallyDeletedItems(connectedApplications, result);
        aggregateResult.push({ deletedItems: deletedConnectedApplications, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          connectedApplications,
          result,
          {
            success: standardActionSuccessMsg(Messages.connectedApplicationsConnectedApplicationDeleted),
            error: standardActionErrorMsg(Messages.connectedApplicationsConnectedApplicationDeleteError),
          },
          (errorItem: IErrorItem<IConnectedApplicationInfo>) => null //ConnectedApplicationErrors.errorDetailsFor(errorItem)
        );
      });
  }
  return Promise.all(outstandingPromises).then(() => {
    return combineIDeleteContentsResults(aggregateResult);
  });
}

function getLocalizedContentType(contentType: ExplorableItemType): string {
  const typeDefinition = getExplorableItemDefinition(contentType);
  if (isUndefined(typeDefinition)) {
    throw new Error('Tried to localize unrecognized content type string: ' + contentType);
  }
  return typeDefinition.singularName();
}

function buildConfirmationDialog(contents: IContentInfo[], workbooksToBeDeleted: IWorkbookParentInfo[]): string {
  let dialogBody = '';
  if (contents.length === 1) {
    let contentName;
    if (contents[0].contentType === 'runFlowTask') {
      const runFlowTaskContent: RunFlowTaskInfo = contents[0] as unknown as RunFlowTaskInfo;
      if (runFlowTaskContent.taskType === 'LinkedTask') {
        return Messages.linkedTasksDeleteWarning();
      } else {
        contentName = runFlowTaskContent.flow.name;
      }
    } else {
      contentName = contents[0].name;
    }

    if (contents[0].contentType === 'schedule') {
      return DeleteSchedulesActionService.deleteSingleScheduleBodyText(contents[0] as unknown as IScheduleInfo);
    }

    dialogBody += Messages.confirmDeleteContent({
      CONTENT_NAME: L10n.elideIfLong(contentName),
      CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(contents[0].contentType))
    });

    switch (contents[0].contentType) {
      case 'project':
        dialogBody += Messages.deleteProjectWarning();
        break;
      case 'collection':
        dialogBody += Messages.deleteCollectionWarning();
        break;
      case 'view':
        dialogBody += Messages.deleteViewWarning();
        break;
      case 'connectedApplication':
        dialogBody += ' ' + Messages.deleteConnectedApplicationWarning({
          ITEM_COUNT: contents.length
        });
    }
  } else {
    dialogBody += Messages.confirmDeleteContents({
      CONTENT_COUNT_LOC: Formatters.formatCount(contents.length)
    });
  }

  if (workbooksToBeDeleted.length > 0) {
    const workbooksWarning = Messages.confirmDeleteViewsWorkbookWarning({
      WORKBOOK_COUNT: workbooksToBeDeleted.length,
      WORKBOOK_COUNT_LOC: Formatters.formatCount(workbooksToBeDeleted.length),
      WORKBOOK_NAME: L10n.elideIfLong(workbooksToBeDeleted[0].name)
    });
    dialogBody = dialogBody + ' ' + workbooksWarning;
  }
  return dialogBody;
}


// exported for testing
export function getActuallyDeletedItems(items: Set<IContentInfo<ExplorableItemType>>, result: IResult): ContentTypeAndID[] {
  const deletedItems: ContentTypeAndID[] = [];
  items.each((item: IContentInfo<ExplorableItemType>) => {
    if (result.errors && !_.any(result.errors, (error: IError) => {
      return (item.contentType === 'collection') ? (error.id === item.luid) : (error.id === item.id);
    })) {
      deletedItems.push({ id: item.id, contentType: item.contentType });
    }
  });
  return deletedItems;
}

function combineIDeleteContentsResults(results: IDeleteContentsResult[]): IDeleteContentsResult {
  return {
    deletedItems: ResultHelper.combine(results, (result) => result.deletedItems),
    errors: ResultHelper.combineErrors(results)
  };
}

export const DeleteContentsAction = {
  confirmDeleteContents,
  determineWorkbooksToBeDeleted
};

export type DeleteContentsAction = typeof DeleteContentsAction;
